import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getAllLedgerAccounts,
  getDerasarPersonUsers,
  getDonationCategories,
  getOpeningBalance,
  lockOpeningBalance,
  unlockOpeningBalance,
  updateOBAssets,
  updateOBCategories,
  updateOBOfferings,
} from '../../../services/api';
import { normalizeDateOnly } from '../../../utils/dateUtils';
import { buildPersonOptions, getNumericAmount } from './openingBalanceUtils';

// Under cash-basis OB there are no auto-synced "locked" accounts. The hook keeps
// this helper as a no-op so the OpeningBalancePage doesn't have to special-case.
const isLockedOpeningLedgerAccount = () => false;

export default function useOpeningBalanceData({ activeDerasarId, yearId, reportOnly = false }) {
  const [fy, setFy] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [personUsers, setPersonUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [inputsEditable, setInputsEditable] = useState(false);
  const [assetLines, setAssetLines] = useState({});
  const [categoryLines, setCategoryLines] = useState({});
  const [offeringLines, setOfferingLines] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Always use the OB endpoint — it handles both the first FY (manual OB) and
      // subsequent FYs (carry-forward from previous year closing) in one consistent shape.
      const obRes = await getOpeningBalance(yearId);

      const resolvedDerasarId = obRes.data.derasarId || activeDerasarId;
      if (!resolvedDerasarId) throw new Error('Derasar not found for opening balance');

      const [accountsRes, categoriesRes, usersRes] = await Promise.all([
        getAllLedgerAccounts(resolvedDerasarId),
        getDonationCategories(resolvedDerasarId),
        reportOnly ? Promise.resolve({ data: [] }) : getDerasarPersonUsers(resolvedDerasarId),
      ]);

      const normalizedUsers = buildPersonOptions(usersRes.data, 'User');

      setFy({
        id: String(yearId),
        yearLabel: obRes.data.yearLabel,
        obStatus: obRes.data.openingBalanceState?.status || obRes.data.obStatus || 'draft',
        balanceDifference: obRes.data.openingBalanceState?.balanceDifference || 0,
        startDate: normalizeDateOnly(obRes.data.startDate),
        endDate: normalizeDateOnly(obRes.data.endDate),
      });

      setAccounts(accountsRes.data || []);
      setCategories(categoriesRes.data || []);
      setPersonUsers(normalizedUsers);
      // In report mode: force canEdit/inputsEditable to false regardless of what the API says.
      setCanEdit(reportOnly ? false : !!obRes.data.canEdit);
      setInputsEditable(reportOnly ? false : !!obRes.data.isInputsEditable);

      const nextAssetLines = {};
      const rawAssets = Array.isArray(obRes.data.assets) ? obRes.data.assets : [];
      rawAssets.forEach((asset) => {
        const accountId = asset.ledgerAccountId?.id || asset.ledgerAccount?.id || asset.ledgerAccountId;
        nextAssetLines[accountId] = {
          amountInr: asset.amountInr != null ? String(asset.amountInr) : '',
          nature: asset.nature || null,
        };
      });
      setAssetLines(nextAssetLines);

      const nextCategoryLines = {};
      (Array.isArray(obRes.data.categories) ? obRes.data.categories : []).forEach((category) => {
        const categoryId = category.donationCategoryId?.id || category.DonationCategory?.id || category.donationCategoryId;
        nextCategoryLines[categoryId] = {
          fundBalanceInr: category.fundBalanceInr != null ? String(category.fundBalanceInr) : '',
        };
      });
      setCategoryLines(nextCategoryLines);

      const nextOfferingLines = {};
      const openingOfferings = Array.isArray(obRes.data.offerings) ? obRes.data.offerings
        : Array.isArray(obRes.data.pledges) ? obRes.data.pledges : [];
      openingOfferings.forEach((offering) => {
        const categoryId = offering.donationCategoryId?.id || offering.donationCategoryId;
        const key = `${offering.userId}:${categoryId}:${offering.id}`;
        nextOfferingLines[key] = {
          id: String(offering.id || ''),
          offerNo: offering.offerNo || '',
          userId: String(offering.userId || ''),
          userName: offering.userId?.name || offering.member?.name || offering.creator?.name || '',
          donationCategoryId: String(categoryId || ''),
          pendingAmountInr: offering.pendingAmountInr != null
            ? String(offering.pendingAmountInr)
            : String(offering.pendingAmount || ''),
          originalOfferingDate: normalizeDateOnly(offering.originalOfferingDate || offering.offeringDate),
        };
      });
      setOfferingLines(nextOfferingLines);
    } catch {
      toast.error('Failed to load opening balance data');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId, reportOnly, yearId]);

  useEffect(() => {
    load();
  }, [load]);

  const assetAccounts = useMemo(
    () => [...accounts].sort((left, right) => (left.accountCode || '').localeCompare(right.accountCode || '')),
    [accounts]
  );

  const sortedCategories = useMemo(
    () => [...categories].sort((left, right) => (left.name || '').localeCompare(right.name || '')),
    [categories]
  );

  const offeringRows = useMemo(() => Object.entries(offeringLines), [offeringLines]);

  const openingOfferingTotal = useMemo(
    () => Object.values(offeringLines).reduce((sum, line) => sum + getNumericAmount(line.pendingAmountInr), 0),
    [offeringLines],
  );

  // Cash-basis OB: user-entered values flow through directly.
  const effectiveAssetLines = assetLines;

  const structureTotals = useMemo(() => {
    const totals = {
      assetTotal: 0,
      liabilityLedgerTotal: 0,
      equityLedgerTotal: 0,
      incomeLedgerTotal: 0,
      expenditureLedgerTotal: 0,
    };

    assetAccounts.forEach((account) => {
      const rawAmount = getNumericAmount(effectiveAssetLines[account.id]?.amountInr);
      const obNature = effectiveAssetLines[account.id]?.nature;
      const normalBalance = account.group?.normalBalance;
      const defaultNature = normalBalance === 'Cr' ? 'credit' : 'debit';
      const effectiveNature = obNature || defaultNature;
      const amount = effectiveNature === defaultNature ? rawAmount : -rawAmount;
      const nature = account.group?.nature;

      if (nature === 'asset') totals.assetTotal += amount;
      else if (nature === 'liability') totals.liabilityLedgerTotal += amount;
      else if (nature === 'equity') totals.equityLedgerTotal += amount;
      else if (nature === 'income') totals.incomeLedgerTotal += amount;
      else if (nature === 'expenditure') totals.expenditureLedgerTotal += amount;
    });

    // Standard balance equation: Assets = Liabilities + Equity + (Income − Expense)
    const netIncome = parseFloat((totals.incomeLedgerTotal - totals.expenditureLedgerTotal).toFixed(2));
    totals.netIncome = netIncome;
    totals.liabilitiesEquityTotal =
      totals.liabilityLedgerTotal + totals.equityLedgerTotal + netIncome;
    totals.balanceDifference = parseFloat((totals.assetTotal - totals.liabilitiesEquityTotal).toFixed(2));

    // Display-only totals consumed by Category and Offering tabs.
    totals.categoryFundTotal = Object.values(categoryLines).reduce(
      (sum, line) => sum + getNumericAmount(line.fundBalanceInr), 0,
    );
    totals.offeringBalanceTotal = Object.values(offeringLines).reduce(
      (sum, line) => sum + getNumericAmount(line.pendingAmountInr), 0,
    );

    return totals;
  }, [assetAccounts, effectiveAssetLines, categoryLines, offeringLines]);

  const reportData = useMemo(() => {
    const buildGroupedSection = (nature) => {
      const subGroupMap = new Map();

      assetAccounts
        .filter((account) => account.group?.nature === nature)
        .forEach((account) => {
          const balance = getNumericAmount(effectiveAssetLines[account.id]?.amountInr);

          const groupName = account.subGroup?.name || 'Other';
          const displayOrder = account.subGroup?.displayOrder ?? 999;
          const existing = subGroupMap.get(groupName) || {
            name: groupName,
            displayOrder,
            rows: [],
            subtotal: 0,
          };

          existing.rows.push({
            id: account.id,
            name: account.name,
            accountCode: account.accountCode,
            balance,
          });
          existing.subtotal += balance;
          subGroupMap.set(groupName, existing);
        });

      const subGroups = [...subGroupMap.values()]
        .sort((left, right) => left.displayOrder - right.displayOrder || left.name.localeCompare(right.name))
        .map((group) => ({
          ...group,
          rows: group.rows.sort(
            (left, right) => (left.accountCode || '').localeCompare(right.accountCode || '') || left.name.localeCompare(right.name)
          ),
        }));

      return {
        subGroups,
        total: subGroups.reduce((sum, group) => sum + group.subtotal, 0),
      };
    };

    const equityRows = assetAccounts
      .filter((account) => account.group?.nature === 'equity')
      .map((account) => ({
        id: account.id,
        name: account.name,
        accountCode: account.accountCode,
        balance: getNumericAmount(effectiveAssetLines[account.id]?.amountInr),
      }))
      .sort((left, right) => (left.accountCode || '').localeCompare(right.accountCode || '') || left.name.localeCompare(right.name));

    const incomeRows = assetAccounts
      .filter((account) => account.group?.nature === 'income')
      .map((account) => ({
        id: account.id,
        name: account.name,
        accountCode: account.accountCode,
        balance: getNumericAmount(effectiveAssetLines[account.id]?.amountInr),
      }))
      .sort((left, right) => (left.accountCode || '').localeCompare(right.accountCode || '') || left.name.localeCompare(right.name));

    const expenditureRows = assetAccounts
      .filter((account) => account.group?.nature === 'expenditure')
      .map((account) => ({
        id: account.id,
        name: account.name,
        accountCode: account.accountCode,
        balance: getNumericAmount(effectiveAssetLines[account.id]?.amountInr),
      }))
      .sort((left, right) => (left.accountCode || '').localeCompare(right.accountCode || '') || left.name.localeCompare(right.name));

    const totalIncome = incomeRows.reduce((sum, row) => sum + row.balance, 0);
    const totalExpenditure = expenditureRows.reduce((sum, row) => sum + row.balance, 0);
    const surplus = totalIncome - totalExpenditure;
    const totalLiabilitiesEquity = structureTotals.liabilityLedgerTotal + structureTotals.equityLedgerTotal + surplus;

    return {
      assets: buildGroupedSection('asset'),
      liabilities: buildGroupedSection('liability'),
      equity: {
        rows: equityRows,
        total: equityRows.reduce((sum, row) => sum + row.balance, 0),
      },
      incomeExpense: {
        incomeRows,
        expenditureRows,
        totalIncome,
        totalExpenditure,
        surplus,
      },
      totalLiabilitiesEquity,
      isBalanced: Math.abs(structureTotals.assetTotal - totalLiabilitiesEquity) < 0.01,
      difference: parseFloat(
        (structureTotals.assetTotal - totalLiabilitiesEquity).toFixed(2)
      ),
    };
  }, [assetAccounts, effectiveAssetLines, structureTotals]);

  const setAssetField = useCallback((accountId, field, value) => {
    setAssetLines((prev) => ({ ...prev, [accountId]: { ...prev[accountId], [field]: value } }));
  }, []);

  const setCategoryField = useCallback((categoryId, value) => {
    setCategoryLines((prev) => ({ ...prev, [categoryId]: { ...prev[categoryId], fundBalanceInr: value } }));
  }, []);

  const setOfferingField = useCallback((key, field, value) => {
    setOfferingLines((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  }, []);

  const addOfferingRow = useCallback(() => {
    const key = `new-${Date.now()}`;
    setOfferingLines((prev) => ({
      ...prev,
      [key]: {
        id: '',
        userId: '',
        userName: '',
        donationCategoryId: '',
        pendingAmountInr: '',
        originalOfferingDate: '',
      },
    }));
  }, []);

  const persistOfferingLines = useCallback(async (nextOfferingLines, successMessage = 'Opening offerings saved') => {
    setSaving(true);
    try {
      const lines = Object.values(nextOfferingLines)
        .map((line) => ({
          id: line.id || null,
          userId: line.userId,
          donationCategoryId: line.donationCategoryId,
          pendingAmountInr: getNumericAmount(line.pendingAmountInr),
          originalOfferingDate: line.originalOfferingDate || null,
          isEstimated: false,
          sourceReference: null,
        }))
        .filter((line) => line.userId && line.donationCategoryId && line.pendingAmountInr > 0);

      await updateOBOfferings(yearId, { lines });
      toast.success(successMessage);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [load, yearId]);

  const addOfferingRowWithData = useCallback(async (data) => {
    const key = `new-${Date.now()}`;
    const nextLines = {
      ...offeringLines,
      [key]: {
        id: '',
        userId: data.userId || '',
        userName: data.userName || '',
        donationCategoryId: data.donationCategoryId || '',
        pendingAmountInr: data.pendingAmountInr || '',
        originalOfferingDate: data.originalOfferingDate || '',
      },
    };
    await persistOfferingLines(nextLines, 'Opening offering added');
  }, [offeringLines, persistOfferingLines]);

  const addOfferingRowsWithData = useCallback(async (rows) => {
    const nextLines = { ...offeringLines };

    rows.forEach((data, index) => {
      const key = `new-${Date.now()}-${index}`;
      nextLines[key] = {
        id: '',
        userId: data.userId || '',
        userName: data.userName || '',
        donationCategoryId: data.donationCategoryId || '',
        pendingAmountInr: data.pendingAmountInr || '',
        originalOfferingDate: data.originalOfferingDate || '',
      };
    });

    await persistOfferingLines(nextLines, 'Opening offerings added');
  }, [offeringLines, persistOfferingLines]);

  const updateOfferingRow = useCallback(async (key, data) => {
    const nextLines = {
      ...offeringLines,
      [key]: { ...offeringLines[key], ...data },
    };
    await persistOfferingLines(nextLines, 'Opening offering updated');
  }, [offeringLines, persistOfferingLines]);

  const removeOfferingRow = useCallback(async (key) => {
    const nextLines = { ...offeringLines };
    delete nextLines[key];
    await persistOfferingLines(nextLines, 'Opening offering removed');
  }, [offeringLines, persistOfferingLines]);

  const handleOfferingMemberSelect = useCallback((key, person) => {
    setOfferingLines((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        userId: String(person.id),
        userName: person.name || '',
      },
    }));
  }, []);

  const saveAssets = useCallback(async () => {
    setSaving(true);
    try {
      const lines = assetAccounts
        .map((account) => {
          const line = effectiveAssetLines[account.id] || {};
          const normalBalance = account.group?.normalBalance;
          const defaultNature = normalBalance === 'Cr' ? 'credit' : 'debit';
          return {
            ledgerAccountId: account.id,
            amountInr: getNumericAmount(line.amountInr),
            nature: line.nature || defaultNature,
            goldGrams: null,
            goldPurity: null,
            valuationRate: null,
            isEstimated: false,
            sourceReference: null,
          };
        })
        .filter((line) => line.amountInr > 0);

      await updateOBAssets(yearId, { lines });
      toast.success('Asset opening balances saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [assetAccounts, effectiveAssetLines, load, yearId]);

  const saveCategories = useCallback(async () => {
    setSaving(true);
    try {
      const lines = categories
        .map((category) => ({
          donationCategoryId: category.id,
          fundBalanceInr: getNumericAmount(categoryLines[category.id]?.fundBalanceInr),
          isEstimated: false,
          sourceReference: null,
          notes: null,
        }))
        .filter((line) => line.fundBalanceInr !== 0);

      await updateOBCategories(yearId, { lines });
      toast.success('Category fund balances saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [categories, categoryLines, load, yearId]);

  const saveOfferings = useCallback(async () => {
    await persistOfferingLines(offeringLines);
  }, [offeringLines, persistOfferingLines]);

  const handleLock = useCallback(async () => {
    // Soft balance check warning before locking
    const diff = Math.abs(structureTotals.balanceDifference || 0);
    if (diff > 0.009) {
      const ok = window.confirm(
        `Opening balance has a difference of ₹${diff.toFixed(2)}.\n\n` +
        `Assets do not fully equal Liabilities + Equity + Net Income.\n\n` +
        `You can still lock — the difference will be recorded. Would you like to proceed?`
      );
      if (!ok) return;
    }
    try {
      const res = await lockOpeningBalance(yearId);
      const msg = res?.data?.data?.message || res?.data?.message || 'Opening balance locked';
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to lock');
    }
  }, [load, yearId, structureTotals.balanceDifference]);

  const handleUnlock = useCallback(async () => {
    try {
      await unlockOpeningBalance(yearId);
      toast.success('Opening balance unlocked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to unlock');
    }
  }, [load, yearId]);

  return {
    fy,
    accounts: assetAccounts,
    categories,
    sortedCategories,
    personUsers,
    loading,
    saving,
    canEdit,
    inputsEditable,
    assetLines: effectiveAssetLines,
    categoryLines,
    offeringRows,
    structureTotals,
    reportData,
    isLockedOpeningLedgerAccount,
    setAssetField,
    setCategoryField,
    setOfferingField,
    addOfferingRow,
    addOfferingRowWithData,
    addOfferingRowsWithData,
    updateOfferingRow,
    removeOfferingRow,
    handleOfferingMemberSelect,
    saveAssets,
    saveCategories,
    saveOfferings,
    handleLock,
    handleUnlock,
  };
}
